import { minusDays, monthKey } from '../shared/iso-day';
import type { NonEmpty } from '../shared/non-empty';

/**
 * The floor every observation consumer applies to its lead-in: `max(need, DECODE_CONTEXT_DAYS)`.
 * With it the first month read is a full month before the window, so the decoder's Apohja
 * list-price correction (`APOHJA_MAX_CORRECTION_DAYS = 21` in observation.ts, which carries
 * `prevLp` forward across days) yields the same result inside the window as a whole-history decode.
 * The Apohja scan also measures a dip's length forward (`scanApohjaIntervals`), so a window ending
 * in the past needs `DECODE_CONTEXT_DAYS` of lead-out after it as well.
 */
export const DECODE_CONTEXT_DAYS = 28;

/** The earliest day any observation read may ask for; earlier lead-ins are clamped to it. */
export const OBSERVATION_HISTORY_START = '2020-01-01';

/**
 * The YYYY-MM months from the month of `from - leadInDays` to the month of
 * `to + leadOutDays`: ascending, contiguous, de-duplicated. Days are YYYY-MM-DD, read as UTC.
 *
 * The start is clamped to `OBSERVATION_HISTORY_START` (or to `to` when `to` precedes it), so a
 * typed early start stays bounded. The lead-out stops at the current UTC day and never moves the
 * end before `to`: a window ending today adds nothing. Throws when `from > to`; callers guard an
 * empty range themselves.
 */
export function monthsForDayRange(
  from: string,
  to: string,
  leadInDays = 0,
  leadOutDays = 0
): NonEmpty<string> {
  if (from > to) {
    throw new Error(`monthsForDayRange: from ${from} is after to ${to}`);
  }
  const floor = to < OBSERVATION_HISTORY_START ? to : OBSERVATION_HISTORY_START;
  const leadIn = minusDays(from, leadInDays);
  const startDay = leadIn < floor ? floor : leadIn;
  const today = new Date().toISOString().slice(0, 10);
  const leadOut = minusDays(to, -leadOutDays);
  const capped = leadOut > today ? today : leadOut;
  const endDay = capped > to ? capped : to;
  const start = new Date(`${startDay}T00:00:00Z`);
  const last = monthKey(endDay);
  const first = monthKey(startDay);
  const rest: string[] = [];
  const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  for (let ym = monthKey(cur.toISOString().slice(0, 10)); ym <= last; ) {
    rest.push(ym);
    cur.setUTCMonth(cur.getUTCMonth() + 1);
    ym = monthKey(cur.toISOString().slice(0, 10));
  }
  return [first, ...rest];
}
